import { useState, useEffect, useMemo } from 'react'
import { X, Search } from 'lucide-react'
import type { UIProductFilters, WeightOption, StrainGroup, PriceRange } from '@/types'
import { useCategories, useTypes, useUnits, type Brand, type ProductType } from '@/api/products'

const WEIGHT_OPTIONS: WeightOption[] = ['1g', '3.5g', '7g', '14g', '28g']
// Display order for the four strain chips. Each chip toggles ALL the
// type IDs in its group (computed at render time from /objects/types
// — see groupStrainTypes below). Chips with zero matching types are
// hidden so we don't render a "Blend" chip on tenants that don't have
// the row yet.
const STRAIN_GROUPS: StrainGroup[] = ['Indica', 'Sativa', 'Hybrid', 'Blend']
const PRICE_RANGES: { value: PriceRange; label: string }[] = [
  { value: 'under20', label: 'Under $20' },
  { value: '20to40', label: '$20 - $40' },
  { value: '40to80', label: '$40 - $80' },
  { value: 'over80', label: '$80+' },
]

// Classify a fetched ProductType into one of the four strain chip
// groups by name. Done by regex rather than hardcoded IDs because Type
// PKs differ per environment. "Indica Dominant" / "Sativa Dominant"
// fall under their parent. "Hybrid" is exact-match (case-insensitive)
// so a future "Indica Hybrid" row would land in Indica, not Hybrid.
// Returns null for types that don't match any group (e.g. "CBD" or
// other specialty rows) — those are filterable via search but get no
// chip in the modal.
function classifyStrainType(name: string): StrainGroup | null {
  const n = name.trim()
  if (n.toLowerCase() === 'hybrid') return 'Hybrid'
  if (/indica/i.test(n)) return 'Indica'
  if (/sativa/i.test(n)) return 'Sativa'
  if (/blend/i.test(n)) return 'Blend'
  return null
}

// Build a chip-group → type-ID list mapping. Stable order (Indica,
// Sativa, Hybrid, Blend) regardless of fetch order.
function groupStrainTypes(types: ProductType[]): Record<StrainGroup, number[]> {
  const groups: Record<StrainGroup, number[]> = {
    Indica: [],
    Sativa: [],
    Hybrid: [],
    Blend: [],
  }
  for (const t of types) {
    const g = classifyStrainType(t.name)
    if (g) groups[g].push(t.id)
  }
  return groups
}

// Preset cannabinoid % ranges. Each row maps to a [min, max] tuple over [0, 100].
const POTENCY_PRESETS: { label: string; range: [number, number] }[] = [
  { label: 'Any', range: [0, 100] },
  { label: '0–10%', range: [0, 10] },
  { label: '10–20%', range: [10, 20] },
  { label: '20–30%', range: [20, 30] },
  { label: '30%+', range: [30, 100] },
]

export interface ProductFiltersModalProps {
  filters: UIProductFilters
  onFiltersChange: (filters: UIProductFilters) => void
  onClose: () => void
  brands: Brand[]
}

export function ProductFiltersModal({
  filters,
  onFiltersChange,
  onClose,
  brands,
}: ProductFiltersModalProps) {
  const [localFilters, setLocalFilters] = useState<UIProductFilters>(filters)
  const [brandSearch, setBrandSearch] = useState('')
  const [showAllBrands, setShowAllBrands] = useState(false)
  const [categorySearch, setCategorySearch] = useState('')
  const [showAllCategories, setShowAllCategories] = useState(false)
  const [unitSearch, setUnitSearch] = useState('')
  const [showAllUnits, setShowAllUnits] = useState(false)

  const { data: categoryGroups = [] } = useCategories()
  const { data: productTypes = [] } = useTypes()
  const { data: productUnits = [] } = useUnits()
  const PREVIEW_LIMIT = 10

  const strainGroupIds = useMemo(() => groupStrainTypes(productTypes), [productTypes])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose() }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const handleApply = () => {
    onFiltersChange(localFilters)
    onClose()
  }

  const handleReset = () => {
    const defaultFilters: UIProductFilters = {
      weights: [],
      productTypes: [],
      thcRange: [0, 100],
      cbdRange: [0, 100],
      brands: [],
      priceRanges: [],
      categoryGroups: [],
      units: [],
    }
    setLocalFilters(defaultFilters)
    setBrandSearch('')
    setShowAllBrands(false)
    setCategorySearch('')
    setShowAllCategories(false)
    setUnitSearch('')
    setShowAllUnits(false)
  }

  // ---------- Toggle helpers ----------
  const toggleWeight = (weight: WeightOption) => {
    setLocalFilters((prev) => ({
      ...prev,
      weights: prev.weights.includes(weight)
        ? prev.weights.filter((w) => w !== weight)
        : [...prev.weights, weight],
    }))
  }

  const toggleStrainGroup = (group: StrainGroup) => {
    const groupIds = strainGroupIds[group]
    if (groupIds.length === 0) return
    setLocalFilters((prev) => {
      const groupSet = new Set(groupIds)
      const anySelected = prev.productTypes.some((id) => groupSet.has(id))
      const next = anySelected
        ? prev.productTypes.filter((id) => !groupSet.has(id))
        : [...prev.productTypes, ...groupIds]
      return { ...prev, productTypes: next }
    })
  }

  const toggleUnit = (unitId: number) => {
    setLocalFilters((prev) => ({
      ...prev,
      units: prev.units.includes(unitId)
        ? prev.units.filter((id) => id !== unitId)
        : [...prev.units, unitId],
    }))
  }

  const toggleBrand = (brandId: number) => {
    setLocalFilters((prev) => ({
      ...prev,
      brands: prev.brands.includes(brandId)
        ? prev.brands.filter((id) => id !== brandId)
        : [...prev.brands, brandId],
    }))
  }

  const togglePriceRange = (range: PriceRange) => {
    setLocalFilters((prev) => ({
      ...prev,
      priceRanges: prev.priceRanges.includes(range)
        ? prev.priceRanges.filter((r) => r !== range)
        : [...prev.priceRanges, range],
    }))
  }

  const toggleCategoryGroup = (id: number) => {
    setLocalFilters((prev) => ({
      ...prev,
      categoryGroups: prev.categoryGroups.includes(id)
        ? prev.categoryGroups.filter((c) => c !== id)
        : [...prev.categoryGroups, id],
    }))
  }

  const setThcRange = ([min, max]: [number, number]) => {
    setLocalFilters((prev) => ({ ...prev, thcRange: [min, max] }))
  }

  const setCbdRange = ([min, max]: [number, number]) => {
    setLocalFilters((prev) => ({ ...prev, cbdRange: [min, max] }))
  }

  // ---------- Brand list with search + selected-first ordering ----------
  // Selected brands always render first so the user can see/deselect them
  // even after typing a search query that wouldn't match. Mirrors the
  // category/unit rails: 10-chip preview, expandable, search bypasses the
  // collapse so typed queries reveal all matches.
  const filteredBrands = useMemo(() => {
    const q = brandSearch.trim().toLowerCase()
    const selectedSet = new Set(localFilters.brands)
    const matchesQuery = (b: Brand) => !q || b.name.toLowerCase().includes(q)
    const selected = brands.filter((b) => selectedSet.has(b.id) && matchesQuery(b))
    const unselectedAll = brands.filter((b) => !selectedSet.has(b.id) && matchesQuery(b))
    const isSearching = q.length > 0
    const collapse = !showAllBrands && !isSearching
    const unselected = collapse ? unselectedAll.slice(0, PREVIEW_LIMIT) : unselectedAll
    return {
      selected,
      unselected,
      hiddenCount: collapse ? Math.max(0, unselectedAll.length - PREVIEW_LIMIT) : 0,
      isSearching,
    }
  }, [brands, localFilters.brands, brandSearch, showAllBrands])

  // Mirror the brand pattern for category groups: selected first, query
  // filter, then collapse the unselected tail to a 10-chip preview unless
  // the user expanded it or is actively searching. Selected chips always
  // remain visible so a hidden filter can't silently apply.
  const filteredCategories = useMemo(() => {
    const q = categorySearch.trim().toLowerCase()
    const selectedSet = new Set(localFilters.categoryGroups)
    const matchesQuery = (name: string) => !q || name.toLowerCase().includes(q)
    const selected = categoryGroups.filter((c) => selectedSet.has(c.id) && matchesQuery(c.name))
    const unselectedAll = categoryGroups.filter((c) => !selectedSet.has(c.id) && matchesQuery(c.name))
    const isSearching = q.length > 0
    const collapse = !showAllCategories && !isSearching
    const unselected = collapse ? unselectedAll.slice(0, PREVIEW_LIMIT) : unselectedAll
    return {
      selected,
      unselected,
      hiddenCount: collapse ? Math.max(0, unselectedAll.length - PREVIEW_LIMIT) : 0,
      isSearching,
    }
  }, [categoryGroups, localFilters.categoryGroups, categorySearch, showAllCategories])

  // Same collapse-with-search pattern as categories, applied to units.
  // Units come from /objects/units and can run to 80+ rows on cannabis
  // tenants, so the chip rail needs the same affordance.
  const filteredUnits = useMemo(() => {
    const q = unitSearch.trim().toLowerCase()
    const selectedSet = new Set(localFilters.units)
    const matchesQuery = (name: string) => !q || name.toLowerCase().includes(q)
    const selected = productUnits.filter((u) => selectedSet.has(u.id) && matchesQuery(u.name))
    const unselectedAll = productUnits.filter((u) => !selectedSet.has(u.id) && matchesQuery(u.name))
    const isSearching = q.length > 0
    const collapse = !showAllUnits && !isSearching
    const unselected = collapse ? unselectedAll.slice(0, PREVIEW_LIMIT) : unselectedAll
    return {
      selected,
      unselected,
      hiddenCount: collapse ? Math.max(0, unselectedAll.length - PREVIEW_LIMIT) : 0,
      isSearching,
    }
  }, [productUnits, localFilters.units, unitSearch, showAllUnits])

  // Count strain GROUPS (chips), not individual type IDs — toggling
  // "Indica" can add 2 IDs to productTypes but the user sees 1 chip.
  const activeStrainGroupCount = STRAIN_GROUPS.reduce((acc, g) => {
    const groupSet = new Set(strainGroupIds[g])
    return acc + (localFilters.productTypes.some((id) => groupSet.has(id)) ? 1 : 0)
  }, 0)

  const selectedActiveCount =
    localFilters.weights.length +
    activeStrainGroupCount +
    localFilters.brands.length +
    localFilters.priceRanges.length +
    localFilters.categoryGroups.length +
    localFilters.units.length +
    (localFilters.thcRange[0] !== 0 || localFilters.thcRange[1] !== 100 ? 1 : 0) +
    (localFilters.cbdRange[0] !== 0 || localFilters.cbdRange[1] !== 100 ? 1 : 0)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center animate-fade-in">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-baseline gap-2">
            <h2 className="text-lg font-semibold text-gray-900">Product Filters</h2>
            {selectedActiveCount > 0 && (
              <span className="text-xs text-gray-500">{selectedActiveCount} active</span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Category Groups — searchable, collapsed to 10 chips by default */}
          {categoryGroups.length > 0 && (
            <FilterSection
              title={`Category Groups${localFilters.categoryGroups.length > 0 ? ` (${localFilters.categoryGroups.length})` : ''}`}
            >
              {categoryGroups.length > PREVIEW_LIMIT && (
                <div className="relative mb-2">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={categorySearch}
                    onChange={(e) => setCategorySearch(e.target.value)}
                    placeholder={`Search ${categoryGroups.length} categories…`}
                    className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:border-green-500 focus:ring-0 outline-none"
                  />
                </div>
              )}

              {filteredCategories.selected.length === 0 && filteredCategories.unselected.length === 0 ? (
                <p className="text-xs text-gray-500 px-1 py-2">No categories match "{categorySearch}".</p>
              ) : (
                <div className="space-y-2">
                  {filteredCategories.selected.length > 0 && (
                    <div className="flex flex-wrap gap-2 pb-2 border-b border-gray-100">
                      {filteredCategories.selected.map((cat) => (
                        <FilterChip
                          key={cat.id}
                          label={cat.name}
                          selected
                          onClick={() => toggleCategoryGroup(cat.id)}
                        />
                      ))}
                    </div>
                  )}
                  {filteredCategories.unselected.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {filteredCategories.unselected.map((cat) => (
                        <FilterChip
                          key={cat.id}
                          label={cat.name}
                          selected={false}
                          onClick={() => toggleCategoryGroup(cat.id)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {!filteredCategories.isSearching && (
                <>
                  {filteredCategories.hiddenCount > 0 && (
                    <button
                      onClick={() => setShowAllCategories(true)}
                      className="mt-2 text-sm font-medium text-green-600 hover:text-green-700 cursor-pointer"
                    >
                      See more ({filteredCategories.hiddenCount})
                    </button>
                  )}
                  {showAllCategories && categoryGroups.length > PREVIEW_LIMIT && (
                    <button
                      onClick={() => setShowAllCategories(false)}
                      className="mt-2 text-sm font-medium text-green-600 hover:text-green-700 cursor-pointer"
                    >
                      Show less
                    </button>
                  )}
                </>
              )}
            </FilterSection>
          )}

          {/* Weight Filter */}
          <FilterSection title="Weight">
            <div className="flex flex-wrap gap-2">
              {WEIGHT_OPTIONS.map((weight) => (
                <FilterChip
                  key={weight}
                  label={weight}
                  selected={localFilters.weights.includes(weight)}
                  onClick={() => toggleWeight(weight)}
                />
              ))}
            </div>
          </FilterSection>

          {/* Price Filter — placed near the top because price is a primary
              filter dimension for cashiers narrowing a large catalog. */}
          <FilterSection title="Price">
            <div className="flex flex-wrap gap-2">
              {PRICE_RANGES.map(({ value, label }) => (
                <FilterChip
                  key={value}
                  label={label}
                  selected={localFilters.priceRanges.includes(value)}
                  onClick={() => togglePriceRange(value)}
                />
              ))}
            </div>
          </FilterSection>

          {/* Strain Filter — chips are grouped, so "Indica" toggles
              both Indica + Indica Dominant type IDs at once. Hidden
              entirely when /objects/types didn't return any matching
              rows (e.g. non-cannabis tenant). */}
          {STRAIN_GROUPS.some((g) => strainGroupIds[g].length > 0) && (
            <FilterSection title="Strain">
              <div className="flex flex-wrap gap-2">
                {STRAIN_GROUPS.filter((g) => strainGroupIds[g].length > 0).map((group) => {
                  const groupSet = new Set(strainGroupIds[group])
                  const selected = localFilters.productTypes.some((id) => groupSet.has(id))
                  return (
                    <FilterChip
                      key={group}
                      label={group}
                      selected={selected}
                      onClick={() => toggleStrainGroup(group)}
                    />
                  )
                })}
              </div>
            </FilterSection>
          )}

          {/* Units Filter — multi-select UOM via /objects/units. Each
              chip is one unit row; selection state is per-id. Distinct
              from the Weight (grams) filter above, which is the legacy
              name-based filter and stays as-is. Collapsed to a 10-chip
              preview with search, mirroring the Category Groups rail. */}
          {productUnits.length > 0 && (
            <FilterSection
              title={`Units${localFilters.units.length > 0 ? ` (${localFilters.units.length})` : ''}`}
            >
              {productUnits.length > PREVIEW_LIMIT && (
                <div className="relative mb-2">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={unitSearch}
                    onChange={(e) => setUnitSearch(e.target.value)}
                    placeholder={`Search ${productUnits.length} units…`}
                    className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:border-green-500 focus:ring-0 outline-none"
                  />
                </div>
              )}

              {filteredUnits.selected.length === 0 && filteredUnits.unselected.length === 0 ? (
                <p className="text-xs text-gray-500 px-1 py-2">No units match "{unitSearch}".</p>
              ) : (
                <div className="space-y-2">
                  {filteredUnits.selected.length > 0 && (
                    <div className="flex flex-wrap gap-2 pb-2 border-b border-gray-100">
                      {filteredUnits.selected.map((unit) => (
                        <FilterChip
                          key={unit.id}
                          label={unit.name}
                          selected
                          onClick={() => toggleUnit(unit.id)}
                        />
                      ))}
                    </div>
                  )}
                  {filteredUnits.unselected.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {filteredUnits.unselected.map((unit) => (
                        <FilterChip
                          key={unit.id}
                          label={unit.name}
                          selected={false}
                          onClick={() => toggleUnit(unit.id)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {!filteredUnits.isSearching && (
                <>
                  {filteredUnits.hiddenCount > 0 && (
                    <button
                      onClick={() => setShowAllUnits(true)}
                      className="mt-2 text-sm font-medium text-green-600 hover:text-green-700 cursor-pointer"
                    >
                      See more ({filteredUnits.hiddenCount})
                    </button>
                  )}
                  {showAllUnits && productUnits.length > PREVIEW_LIMIT && (
                    <button
                      onClick={() => setShowAllUnits(false)}
                      className="mt-2 text-sm font-medium text-green-600 hover:text-green-700 cursor-pointer"
                    >
                      Show less
                    </button>
                  )}
                </>
              )}
            </FilterSection>
          )}

          {/* THC Filter — chip presets */}
          <FilterSection title="THC %">
            <div className="flex flex-wrap gap-2">
              {POTENCY_PRESETS.map(({ label, range }) => (
                <FilterChip
                  key={label}
                  label={label}
                  selected={localFilters.thcRange[0] === range[0] && localFilters.thcRange[1] === range[1]}
                  onClick={() => setThcRange(range)}
                />
              ))}
            </div>
          </FilterSection>

          {/* CBD Filter — chip presets */}
          <FilterSection title="CBD %">
            <div className="flex flex-wrap gap-2">
              {POTENCY_PRESETS.map(({ label, range }) => (
                <FilterChip
                  key={label}
                  label={label}
                  selected={localFilters.cbdRange[0] === range[0] && localFilters.cbdRange[1] === range[1]}
                  onClick={() => setCbdRange(range)}
                />
              ))}
            </div>
          </FilterSection>

          {/* Brands Filter — searchable, collapsed to 10 chips by default */}
          {brands.length > 0 && (
            <FilterSection title={`Brands${localFilters.brands.length > 0 ? ` (${localFilters.brands.length})` : ''}`}>
              {brands.length > PREVIEW_LIMIT && (
                <div className="relative mb-2">
                  <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    value={brandSearch}
                    onChange={(e) => setBrandSearch(e.target.value)}
                    placeholder={`Search ${brands.length} brands…`}
                    className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:bg-white focus:border-green-500 focus:ring-0 outline-none"
                  />
                </div>
              )}

              {filteredBrands.selected.length === 0 && filteredBrands.unselected.length === 0 ? (
                <p className="text-xs text-gray-500 px-1 py-2">No brands match "{brandSearch}".</p>
              ) : (
                <div className="space-y-2">
                  {filteredBrands.selected.length > 0 && (
                    <div className="flex flex-wrap gap-2 pb-2 border-b border-gray-100">
                      {filteredBrands.selected.map((brand) => (
                        <FilterChip
                          key={brand.id}
                          label={brand.name}
                          selected
                          onClick={() => toggleBrand(brand.id)}
                        />
                      ))}
                    </div>
                  )}
                  {filteredBrands.unselected.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {filteredBrands.unselected.map((brand) => (
                        <FilterChip
                          key={brand.id}
                          label={brand.name}
                          selected={false}
                          onClick={() => toggleBrand(brand.id)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {!filteredBrands.isSearching && (
                <>
                  {filteredBrands.hiddenCount > 0 && (
                    <button
                      onClick={() => setShowAllBrands(true)}
                      className="mt-2 text-sm font-medium text-green-600 hover:text-green-700 cursor-pointer"
                    >
                      See more ({filteredBrands.hiddenCount})
                    </button>
                  )}
                  {showAllBrands && brands.length > PREVIEW_LIMIT && (
                    <button
                      onClick={() => setShowAllBrands(false)}
                      className="mt-2 text-sm font-medium text-green-600 hover:text-green-700 cursor-pointer"
                    >
                      Show less
                    </button>
                  )}
                </>
              )}
            </FilterSection>
          )}

        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleReset}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors cursor-pointer"
          >
            Reset All
          </button>
          <button
            onClick={handleApply}
            className="px-6 py-2 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors cursor-pointer"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  )
}

function FilterSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-sm font-bold text-gray-700 mb-2">{title}</h3>
      {children}
    </div>
  )
}

function FilterChip({
  label,
  selected,
  onClick,
}: {
  label: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors cursor-pointer ${
        selected
          ? 'bg-green-500 text-white'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  )
}
